import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Dictation, by whichever route this device has.
 *
 *  - Inside the app shell: the shell listens with the platform recogniser and
 *    posts the words back, because a WebView has no usable speech of its own —
 *    iOS does not expose the Web Speech API to WKWebView at all, and Android's
 *    WebView has no speech service behind it.
 *  - In a desktop browser: webkitSpeechRecognition.
 *  - Anywhere else, or when either is refused: `supported` is false and the
 *    sheet asks the user to type, where the keyboard's mic key still dictates.
 */

const Recognition = typeof window !== "undefined" ? window.SpeechRecognition || window.webkitSpeechRecognition : null;

// running inside the app shell rather than a browser tab
const inAppShell = () => typeof window !== "undefined" && !!window.ReactNativeWebView;

// the shell sets this before the page loads (App.tsx)
const hasNativeBridge = () => inAppShell() && window.__NATIVE_DICTATION__ === true;

/**
 * The browser recogniser is for browsers only. WKWebView exposes
 * webkitSpeechRecognition, but starting it inside an app whose Info.plist has no
 * microphone usage string makes iOS terminate that app — the page would crash
 * the shell it is running in. Older shells, which have no bridge either, must
 * fall through to typing.
 */
const canUseBrowserSpeech = () => !!Recognition && !inAppShell();

export const isSpeechSupported = () => hasNativeBridge() || canUseBrowserSpeech();

const useSpeechInput = ({ onFinal } = {}) => {
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [error, setError] = useState("");
  // the recogniser exists but this device will not let it run: a WebView with
  // no microphone permission, or no speech service behind it
  const [blocked, setBlocked] = useState(false);
  const recognitionRef = useRef(null);
  const silenceRef = useRef(null);
  const capRef = useRef(null);
  const finalRef = useRef("");
  // the newest text seen, final or not, so stopping mid-phrase keeps the words
  const latestRef = useRef("");
  const onFinalRef = useRef(onFinal);
  const native = hasNativeBridge();
  const browserSpeech = canUseBrowserSpeech();

  useEffect(() => {
    onFinalRef.current = onFinal;
  }, [onFinal]);

  /* ------------------------- the app shell ------------------------- */

  const postToShell = useCallback((payload) => {
    try {
      window.ReactNativeWebView.postMessage(JSON.stringify(payload));
    } catch {
      setError("Could not reach the microphone.");
      setListening(false);
    }
  }, []);

  useEffect(() => {
    if (!native) return undefined;

    const handle = (event) => {
      let message;
      try {
        message = JSON.parse(event.data);
      } catch {
        return; // not ours
      }

      switch (message.type) {
        case "DICTATION_STARTED":
          setListening(true);
          setError("");
          break;
        case "DICTATION_PARTIAL":
          latestRef.current = message.text || "";
          setTranscript(latestRef.current);
          break;
        case "DICTATION_RESULT":
          finalRef.current = message.text || "";
          latestRef.current = finalRef.current;
          setTranscript(finalRef.current);
          break;
        case "DICTATION_END": {
          setListening(false);
          const said = (finalRef.current || latestRef.current).trim();
          if (said && onFinalRef.current) onFinalRef.current(said);
          break;
        }
        case "DICTATION_ERROR": {
          setListening(false);

          // iOS reports silence as 1110 "No speech detected" and 203 "Retry",
          // which older shells send as a plain failure. Silence is not a broken
          // microphone, so read the detail rather than trusting the reason.
          const detail = String(message.detail || "");
          const nothingHeard = message.reason === "no-speech" || /no speech|1110|\b203\b|retry|timeout/i.test(detail);
          const cannotEver = !nothingHeard && (message.reason === "denied" || message.reason === "unavailable");

          // only a refusal or a missing recogniser takes the microphone away;
          // everything else is worth another try
          if (cannotEver) setBlocked(true);

          setError(
            message.reason === "denied"
              ? "The microphone is off for this app — turn it on in Settings, or type it."
              : message.reason === "unavailable" && !nothingHeard
              ? "Dictation is not available on this device — type it instead."
              : nothingHeard
              ? "I did not hear anything — tap the mic to try again."
              : "Could not catch that — tap the mic to try again."
          );
          break;
        }
        default:
          break;
      }
    };

    // Android posts to document, iOS to window
    window.addEventListener("message", handle);
    document.addEventListener("message", handle);
    return () => {
      window.removeEventListener("message", handle);
      document.removeEventListener("message", handle);
    };
  }, [native]);

  /* ------------------------- the browser ------------------------- */

  const stop = useCallback(() => {
    if (native) {
      postToShell({ type: "STOP_DICTATION" });
      return;
    }
    const recognition = recognitionRef.current;
    if (recognition) {
      try {
        recognition.stop();
      } catch {
        /* already stopped */
      }
    }
    setListening(false);
  }, [native, postToShell]);

  useEffect(() => stop, [stop]);

  /**
   * End the sentence when the talking stops.
   *
   * Chrome ends by itself after a pause; the platform recognisers behind the
   * shell do not, and would listen until told to stop. Restarting this timer on
   * every new word means a breath mid-sentence is not mistaken for the end.
   */
  useEffect(() => {
    if (!listening) {
      if (silenceRef.current) clearTimeout(silenceRef.current);
      if (capRef.current) clearTimeout(capRef.current);
      silenceRef.current = null;
      capRef.current = null;
      return undefined;
    }

    if (silenceRef.current) clearTimeout(silenceRef.current);
    silenceRef.current = setTimeout(() => stop(), 2500);

    if (!capRef.current) {
      // nothing listens forever, even if no words ever arrive
      capRef.current = setTimeout(() => stop(), 30000);
    }

    return undefined;
  }, [listening, transcript, stop]);

  const start = useCallback(() => {
    setError("");
    setTranscript("");
    finalRef.current = "";
    latestRef.current = "";

    if (native) {
      postToShell({ type: "START_DICTATION" });
      return;
    }

    if (!browserSpeech) {
      setError("This device cannot listen here — type it instead.");
      return;
    }

    const recognition = new Recognition();
    recognition.lang = navigator.language || "en-US";
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;

    recognition.onresult = (event) => {
      let interim = "";
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const chunk = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalRef.current = `${finalRef.current} ${chunk}`.trim();
        } else {
          interim += chunk;
        }
      }
      latestRef.current = `${finalRef.current} ${interim}`.trim();
      setTranscript(latestRef.current);
    };

    recognition.onerror = (event) => {
      // "aborted" is what stopping mid-phrase reports; it is not a failure
      if (event.error === "aborted") return;

      const cannotRun = ["not-allowed", "service-not-allowed", "audio-capture", "network"].includes(event.error);
      if (cannotRun) setBlocked(true);

      setError(
        cannotRun
          ? "No microphone here — type it instead."
          : event.error === "no-speech"
          ? "I did not hear anything."
          : "The microphone stopped unexpectedly."
      );
      setListening(false);
    };

    recognition.onend = () => {
      setListening(false);
      const said = (finalRef.current || latestRef.current).trim();
      if (said && onFinalRef.current) onFinalRef.current(said);
    };

    recognitionRef.current = recognition;
    try {
      recognition.start();
      setListening(true);
    } catch {
      setError("Could not start listening.");
    }
  }, [browserSpeech, native, postToShell]);

  // `supported` means it can actually be used, not merely that it exists
  return {
    supported: (native || browserSpeech) && !blocked,
    blocked,
    listening,
    transcript,
    error,
    start,
    stop,
    setTranscript,
  };
};

export default useSpeechInput;
